import type { ReactNode } from 'react';
import React, { createContext, useContext, useMemo, useRef } from 'react';

type ShortlistPreviewTarget = {
  /** Callback ref the {@link ShortlistPreview} attaches to its avatar stack. */
  registerTarget: (element: HTMLElement | null) => void;
  /** Viewport rect of the registered avatar stack, or null when none is mounted. */
  getTargetRect: () => DOMRect | null;
};

/**
 * Shares the ShortlistPreview's avatar stack with the "fly to contact list"
 * animation triggered from search cards, without threading a ref through the
 * many layers between the two. Consumers rendered outside a provider get a
 * no-op target.
 */
const ShortlistPreviewTargetContext = createContext<ShortlistPreviewTarget>({
  registerTarget: () => {},
  getTargetRect: () => null,
});

export const ShortlistPreviewTargetProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const targetRef = useRef<HTMLElement | null>(null);
  const value = useMemo<ShortlistPreviewTarget>(
    () => ({
      registerTarget: (element) => {
        targetRef.current = element;
      },
      getTargetRect: () => targetRef.current?.getBoundingClientRect() ?? null,
    }),
    []
  );

  return (
    <ShortlistPreviewTargetContext.Provider value={value}>
      {children}
    </ShortlistPreviewTargetContext.Provider>
  );
};

export const useShortlistPreviewTarget = (): ShortlistPreviewTarget =>
  useContext(ShortlistPreviewTargetContext);

export default ShortlistPreviewTargetContext;
